import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { DefaultRootState, useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router';

import { TransactionCurrencyType, WalletConfigurationDto } from 'common/api/autogenerated/client-area';

import { Button } from 'common/components';
import {
  AuthProtectedButton,
  AuthProtectedButtonProps,
} from 'common/components/AuthProtectedLink/AuthProtectedButton.component';
import { ButtonProps } from 'common/components/Button/Button.component';

import { useCustomer, useI18n, usePopup } from 'common/hooks';
import { ConfirmVerificationPopup, DepositForbidenPopup, DepositMethodsPopup } from 'common/popups';
import { requestWalletConfiguration as requestWalletConfigurationApi } from 'common/popups/DepositPopup/helpers';

import { routes } from 'routes/routes.constants';

import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter';

import ApplePayButton from './ApplePay/ApplePayButton.component';
import CreditCardButton from './CreditCard/CreditCardButton.component';
import GooglePayButton from './GooglePay/GooglePayButton.component';
import './index.css';

function isTransactionCurrencyType(value: string) {
  return value === TransactionCurrencyType.FIAT || value === TransactionCurrencyType.CRYPTO;
}

export type DepositMethodKey = 'deposit' | 'wireTransfer' | 'receive';

export type DepositUrls = Record<DepositMethodKey, string>;

export type DepositMethodsProps = {
  urls?: DepositUrls;
  title?: string;
  currency?: string;
  params?: URLSearchParams;
  type?: TransactionCurrencyType | string;
  config?: WalletConfigurationDto;
};

export type DepositFromPages = 'wallet' | 'buyCrypto' | 'exchange' | 'spot' | 'earn' | 'home';

type Props = Pick<AuthProtectedButtonProps, 'pathname'> & {
  className?: string;
  currency?: string;
  amount?: string | number | boolean | null;
  type?: TransactionCurrencyType | string;
  from?: DepositFromPages;
  method?: DepositMethodKey;
  params?: {
    [key: string]: unknown;
  };
  'data-test'?: string;
};

export type DepositButtonComponentProps = {
  currency?: string;
  amount?: number | string;
  disabled?: boolean;
  params?: {
    [key: string]: unknown;
  };
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

interface DepositButtonComponents {
  ApplePay: React.FC<React.PropsWithChildren<DepositButtonComponentProps>>;
  GooglePay: React.FC<React.PropsWithChildren<DepositButtonComponentProps>>;
  CreditCard: React.FC<React.PropsWithChildren<DepositButtonComponentProps>>;
}

const DEFAULT_DEPOSIT_CURRENCY = 'EUR';
const DEFAULT_RECEIVE_CURRENCY = 'BTC';

const DepositButton: React.FC<
  React.PropsWithChildren &
    Props &
    Pick<
      ButtonProps,
      'large' | 'medium' | 'small' | 'color' | 'action' | 'stretch' | 'hidden' | 'fit' | 'outline' | 'disabled'
    >
> &
  DepositButtonComponents = ({
  'data-test': dataTest,
  children,
  currency,
  amount,
  type: propType,
  from,
  method,
  className = '',
  params: propsParams = {},
  pathname,
  ...buttonProps
}) => {
  const routerParams = useParams<{ [key: string]: string }>();
  const currencies = useSelector((state: DefaultRootState) => state.walletReducer.currencies);
  const { translateToText } = useI18n();
  const { push } = useHistory();
  const { show, hide } = usePopup();

  const { isFiatDepositEnabled, isCryptoDepositEnabled } = useCustomer();

  const depositMethodsTitle = useMemo(
    () => capitalizeFirstLetter(translateToText('mobile:deposit:title')),
    [translateToText],
  );

  const type = useMemo(() => {
    if (propType && isTransactionCurrencyType(propType)) return propType;

    if (currency && currencies) {
      const typeFromCurrencies = currencies.find((curr) => curr.code === currency)?.type;

      if (typeFromCurrencies && isTransactionCurrencyType(typeFromCurrencies)) return typeFromCurrencies;
    }

    return undefined;
  }, [propType, currency, currencies]);

  const receiveCurrency = useMemo(
    () => (currency === DEFAULT_DEPOSIT_CURRENCY ? DEFAULT_RECEIVE_CURRENCY : currency),
    [currency],
  );

  const query = useMemo(() => new URLSearchParams(), []);

  if (amount) {
    query.set('amount', String(amount));
  }
  if (from) {
    query.set('fromPage', String(from));
  }

  const params = useMemo(() => ({ ...routerParams, ...propsParams }), [propsParams, routerParams]);

  Object.keys(params).forEach((param: string) => {
    if (params[param]) {
      query.set(param, String(params[param]));
    }
  });

  const urls: DepositUrls = useMemo(
    () => ({
      deposit: `${generatePath(routes.getDepositPage(), {
        currency,
      })}?${query}`,
      wireTransfer: `${generatePath(routes.getWireTransferPage(), {
        currency,
      })}?${query}`,
      receive: `${generatePath(routes.getReceivePage(), {
        currency: receiveCurrency,
      })}?${query}`,
    }),
    [currency, query, receiveCurrency],
  );

  const requestWalletConfiguration = useCallback(
    () => requestWalletConfigurationApi().then((walletConfig) => walletConfig),
    // .catch(() => show(DepositForbidenPopup())),
    [],
  );

  const onClickHandler = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      try {
        const walletConfig = await requestWalletConfiguration();
        const { crypto, fiat } = walletConfig;

        if (
          (!crypto?.isDepositEnabled && !fiat?.isDepositEnabled) ||
          (type === TransactionCurrencyType.CRYPTO && !crypto?.isDepositEnabled) ||
          (type === TransactionCurrencyType.FIAT && !fiat?.isDepositEnabled)
        ) {
          show(DepositForbidenPopup());
          return;
        }

        if (method) {
          if (
            (type === TransactionCurrencyType.CRYPTO && !isCryptoDepositEnabled) ||
            (type === TransactionCurrencyType.FIAT && !isFiatDepositEnabled)
          ) {
            show(ConfirmVerificationPopup({ onCloseHandler: hide }));
            return;
          }
          push(urls[method]);
          return;
        }

        if (type === TransactionCurrencyType.CRYPTO) {
          hide();
          push(urls.receive);
          return;
        }

        show(
          DepositMethodsPopup({
            params: query,
            type,
            title: depositMethodsTitle,
            currency,
            config: walletConfig,
          }),
        );
      } catch (error) {
        console.warn(error);
        show(DepositForbidenPopup());
      }
    },
    [
      requestWalletConfiguration,
      currency,
      depositMethodsTitle,
      hide,
      isCryptoDepositEnabled,
      isFiatDepositEnabled,
      method,
      push,
      query,
      show,
      type,
      urls,
    ],
  );

  const depositButtonClassNames = classNames({
    button: true,
    [className]: className,
  });

  return (
    <AuthProtectedButton pathname={pathname}>
      <Button {...buttonProps} data-test={dataTest} onClick={onClickHandler} className={depositButtonClassNames}>
        {children}
      </Button>
    </AuthProtectedButton>
  );
};

DepositButton.ApplePay = ApplePayButton;
DepositButton.GooglePay = GooglePayButton;
DepositButton.CreditCard = CreditCardButton;

export default DepositButton;
